import React from "react";
import { Target, Eye, Heart, ShieldCheck } from "lucide-react";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/CTA";
import { Seo } from "@/components/Seo";

const stats = [
  { k: "Millions", v: "Pages processed monthly" },
  { k: "99.2%", v: "Extraction accuracy" },
  { k: "Secure", v: "Encrypted end-to-end" },
  { k: "<15s", v: "Avg turnaround" },
];

const values = [
  { icon: Target, title: "Accuracy first", desc: "In healthcare, a wrong digit is a denied claim. We obsess over field-level precision and confidence." },
  { icon: ShieldCheck, title: "Security by default", desc: "Every document is handled with enterprise-grade controls, encryption, and full auditability." },
  { icon: Heart, title: "Built for operators", desc: "We design for the billing teams and claims processors who do the work every day." },
  { icon: Eye, title: "Transparent AI", desc: "Confidence scores and human-in-the-loop review keep humans in control of the outcome." },
];

const AboutUs = () => (
  <PageTransition>
    <Seo
      title="About Us | Hewto.ai"
      description="Hewto.ai is on a mission to end manual claim entry in US healthcare — building AI that turns paper HCFA, UB-04, and dental claims into clean, validated data at scale."
      keywords="about hewto.ai, healthcare document ai company, medical claims automation"
      canonical="https://www.hewto.ai/about-us"
    />
    <PageHero
      eyebrow="About Us"
      title="On a mission to end manual claim entry"
      subtitle="Hewto.ai was founded to give US healthcare a faster, more accurate way to turn paper and PDFs into clean, actionable data."
    />

    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-lg">
            <img
              src="https://images.pexels.com/photos/5452291/pexels-photo-5452291.jpeg"
              alt="Healthcare professional at work"
              className="h-96 w-full object-cover"
            />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Our story</h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              Every day, billions of dollars in US healthcare claims still move on paper — faxed,
              scanned, and keyed by hand. That friction creates denials, delays, and burnout.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              We built Hewto.ai to change that: an intelligent document platform that reads HCFA,
              UB-04, and dental forms with medical precision and delivers clean, validated data your
              systems can act on instantly.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6">
              {stats.map((s) => (
                <div key={s.v}>
                  <div className="text-3xl font-bold text-brand">{s.k}</div>
                  <div className="mt-1 text-sm text-slate-600">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="bg-slate-50 py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <h2 className="text-center text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            What we value
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={(i % 4) * 0.08}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand">
                  <v.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CTABand />
  </PageTransition>
);

export default AboutUs;
